/** Runtime view toggle — the same snapshot data laid out as a flat list or an asset×exchange matrix. */
export const FundingRateBlotterView = { List: 'List', Matrix: 'Matrix' } as const;
export type FundingRateBlotterView = (typeof FundingRateBlotterView)[keyof typeof FundingRateBlotterView];

/** Refresh cadence — Hourly or Daily. Drives the fetch frequency and the polling interval; live and editable from the toolbar or Settings. */
export const FundingRateUpdateMode = { Hourly: 'Hourly', Daily: 'Daily' } as const;
export type FundingRateUpdateMode = (typeof FundingRateUpdateMode)[keyof typeof FundingRateUpdateMode];

/** List-view row grouping. Session-only (not persisted); List view only. */
export const FundingRateGroupBy = { None: 'none', Asset: 'asset', Exchange: 'exchange' } as const;
export type FundingRateGroupBy = (typeof FundingRateGroupBy)[keyof typeof FundingRateGroupBy];

/**
 * One normalized (asset, exchange) row. `fundingRatePct` is already scaled to a percent
 * (e.g. 0.4872 for a 0.004872 fraction); `openInterestUsd` is a raw USD amount. Both are
 * `null` when the exchange reports no value. An asset with no available exchange-asset pairs
 * yields a single "empty" row where `exchange`/`exchangeDisplayName` are undefined and both
 * numeric fields are null.
 */
export interface FundingRateRow {
  id: string;
  symbol: string;
  cmAssetId: string;
  exchange: string | undefined;
  exchangeDisplayName: string | undefined;
  fundingRatePct: number | null;
  openInterestUsd: number | null;
}

/** One exchange column in the Matrix view. */
export interface FundingRateMatrixExchange {
  id: string;
  displayName: string;
}

/** A single Matrix cell's values — rate for the heat chip, open interest for the hover tooltip. */
export interface FundingRateMatrixCell {
  rate: number | null;
  openInterestUsd: number | null;
}

/**
 * One Matrix row — an asset with its current funding rate (and open interest) per exchange. A given
 * exchange is absent from `cellsByExchange` when that (asset, exchange) combination has no data, and
 * the cell renders blank.
 */
export interface FundingRateMatrixRow {
  /** Stable AG Grid row id — the CoinMetrics asset id. */
  id: string;
  symbol: string;
  cmAssetId: string;
  cellsByExchange: Record<string, FundingRateMatrixCell>;
}
